import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Avatar,
  CircularProgress,
  debounce,
  type SlotComponentProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  type PropsFromSlot,
  RichTreeView,
  type RichTreeViewSlots,
  TreeItem,
  TreeItemLabel,
  type TreeViewItemId,
  useTreeViewApiRef,
} from "@mui/x-tree-view";
import { styled } from "@mui/system";
import { HideSourceOutlined } from "@mui/icons-material";
import { getAnnexName } from "./CategorizeUtils";
import { useAnnexData } from "../../../services/hooks/useAnnexData";
import type {
  TreeViewPublicAPI,
  UseTreeViewExpansionSignature,
  UseTreeViewFocusSignature,
  UseTreeViewItemsSignature,
  UseTreeViewKeyboardNavigationSignature,
  UseTreeViewLabelSignature,
  UseTreeViewSelectionSignature,
} from "@mui/x-tree-view/internals";

type ApiRefType = React.RefObject<
  | TreeViewPublicAPI<
      readonly [
        UseTreeViewItemsSignature,
        UseTreeViewExpansionSignature,
        UseTreeViewSelectionSignature,
        UseTreeViewFocusSignature,
        UseTreeViewKeyboardNavigationSignature,
        UseTreeViewLabelSignature,
      ]
    >
  | undefined
>;

const Item = styled(Typography)(() => ({
  padding: "2px 8px;",
  textAlign: "left",
})) as typeof Typography;

const SearchHighlight = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  display: "inline",
  color: theme.palette.primary.main,
})) as typeof Typography;

const ItemLabel = styled(Typography)(() => ({
  padding: "2px 8px;",
  textAlign: "left",
  fontWeight: "bold",
}));

const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  ".MuiTreeItem-iconContainer": {
    paddingTop: theme.spacing(1),
  },
  ".MuiTreeItem-content": {
    alignItems: "stretch",
  },
}));

const HeightRestrictedTreeView = styled(RichTreeView)(() => ({
  height: "600px",
  width: "100%",
  overflow: "auto",
})) as typeof RichTreeView;

function highlightSearchText(text: string, searchText: string) {
  if (typeof searchText !== "string" || searchText.trim() === "") {
    return text;
  }

  // Create a regular expression to match the search value globally, case insensitive
  // Because the regular expression that contains capturing parentheses ( ), matched results are included in the array.
  const regex = new RegExp(`(${searchText})`, "gi");

  // Split results based on search team
  // Replace instances of the search value with the wrapped <b> tag
  return text.split(regex).map((part, index) =>
    part.toLowerCase() === searchText.toLowerCase() ? (
      <SearchHighlight variant="body2" key={index} component="b">
        {part}
      </SearchHighlight>
    ) : (
      part
    ),
  );
}

const renderTreeViewOptionTable = (
  node: ImdrfCodeWithOverrideDTO,
  searchText: string,
) => {
  if (!(node && node.id)) {
    return;
  }
  return (
    <Table
      size="small"
      className="imdrf-table"
      aria-label="a dense table"
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: "none",
        },
      }}
    >
      <TableBody>
        <TableRow>
          <TableCell padding="none" width={"10%"}>
            <ItemLabel variant="body2">Code:</ItemLabel>
          </TableCell>
          <TableCell padding="none" width={"90%"}>
            <Item variant="body2">
              {highlightSearchText(node.code, searchText)}
            </Item>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell padding="none">
            <ItemLabel variant="body2">Term:</ItemLabel>
          </TableCell>
          <TableCell padding="none">
            <Item variant="body2">
              {highlightSearchText(node.term, searchText)}
            </Item>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell padding="none">
            <ItemLabel variant="body2">Definition:</ItemLabel>
          </TableCell>
          <TableCell padding="none">
            <Item variant="body2">
              {highlightSearchText(node.definition, searchText)}
            </Item>
          </TableCell>
        </TableRow>
        {!!node.nonImdrf && (
          <TableRow>
            <TableCell padding="none">
              <ItemLabel variant="body2">Non-IMDRF:</ItemLabel>
            </TableCell>
            <TableCell padding="none">
              <Item variant="body2">
                {highlightSearchText(node.nonImdrf, searchText)}
              </Item>
            </TableCell>
          </TableRow>
        )}
        {!node.isSelectable && (
          <TableRow>
            <TableCell padding="none">
              <ItemLabel variant="body2">Status:</ItemLabel>
            </TableCell>
            <TableCell padding="none">
              <Item variant="body2">Not selectable</Item>
            </TableCell>
          </TableRow>
        )}
        {!node.isSelectable && (
          <TableRow>
            <TableCell padding="none" colSpan={2}>
              <Alert severity="error">
                Please use a more detailed term within the hierarchy.
              </Alert>
            </TableCell>
          </TableRow>
        )}
        {node.isOverridden && node.overrideCode && (
          <TableRow>
            <TableCell padding="none" colSpan={2}>
              <Alert severity="warning">
                {`This code cannot be exported. ${node.overrideCode.code} - ${node.overrideCode.definition} will be used instead if selected.`}
              </Alert>
            </TableCell>
          </TableRow>
        )}
        {node.isOverridden && !node.overrideCode && (
          <TableRow>
            <TableCell padding="none" colSpan={2}>
              <Alert severity="error">
                This code cannot be chosen or exported. Please choose an
                alternate.
              </Alert>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

type TreeItemLabelWithLoadingType = (props: {
  loading: boolean;
  node: ImdrfCodeWithOverrideDTO | ImdrfAnnex;
  searchtext: string;
}) => React.JSX.Element;

const TreeItemLabelWithLoading: TreeItemLabelWithLoadingType = (props) => {
  const { loading, node, searchtext, ...other } = props;
  return (
    <TreeItemLabel {...other}>
      {"term" in node ? (
        renderTreeViewOptionTable(node, searchtext)
      ) : (
        <ItemLabel variant="body2">{node.label}</ItemLabel>
      )}
      {loading && <CircularProgress size="1em" sx={{ marginLeft: 0.5 }} />}
    </TreeItemLabel>
  );
};

interface TreeItemWithLoadingProps extends PropsFromSlot<
  RichTreeViewSlots["item"]
> {
  apiref: ApiRefType;
  itemId: string;
  searchtext: string;
}

const TreeItemWithLoading = forwardRef<HTMLLIElement, TreeItemWithLoadingProps>(
  function TreeItemWithLoadingInternal(props, ref) {
    const apiref: ApiRefType = props.apiref;
    const node: ImdrfAnnex | ImdrfCodeWithOverrideDTO = apiref.current?.getItem(
      props.itemId,
    );

    const labelProps: SlotComponentProps<"div", object, object> & {
      node: ImdrfAnnex | ImdrfCodeWithOverrideDTO;
      searchtext: string;
    } = {
      id: props.itemId,
      node: node,
      searchtext: props.searchtext,
    };

    return (
      <CustomTreeItem
        ref={ref}
        itemId={props.itemId}
        {...props}
        slots={{
          label: TreeItemLabelWithLoading,
        }}
        slotProps={{
          label: labelProps,
        }}
        disabled={!("isSelectable" in node && node.isSelectable)}
      />
    );
  },
);

interface IMDRFComponentInput {
  projectId: string;
  imdrfAnnex: string;
  selectedCode: ImdrfCode | ImdrfCodeWithOverrideDTO | null;
  setSelectedCode: (code: ImdrfCode | ImdrfCodeWithOverrideDTO | null) => void;
}

export default function IMDRFComponent({
  projectId,
  imdrfAnnex,
  selectedCode,
  setSelectedCode,
}: IMDRFComponentInput) {
  const [annexes, setAnnexes] = useState(getCategoryAnnexList(imdrfAnnex));
  const [annexDataFetched, setAnnexDataFetched] = useState(false);
  const [items, setItems] = useState<ImdrfAnnex[]>([]);
  const [inputText, setInputText] = useState("");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const apiRef = useTreeViewApiRef();

  const { getAnnexesData } = useAnnexData();

  function getCategoryAnnexList(imdrfAnnex: string) {
    const annexList: ImdrfAnnex[] = [];
    if (imdrfAnnex) {
      const annexIdList = imdrfAnnex.split(",");
      annexIdList.forEach((a) =>
        annexList.push({
          id: a,
          label: getAnnexName(a),
          children: [],
        }),
      );
    }
    return annexList;
  }

  const codeListContainsSearchText = useCallback(
    (codeList: ImdrfCodeWithOverrideDTO[], searchTerm: string) => {
      return codeList.filter(function (el) {
        return codeContainsSearchText(el, searchTerm);
      });
    },
    [],
  );

  const filterItems = useCallback(
    (searchTerm: string) => {
      const filtered: ImdrfAnnex[] = [];
      const expanded: string[] = [];

      if (searchTerm?.length > 2) {
        searchTerm = searchTerm.toLowerCase();
        //Annex
        const localAnnexes = structuredClone(annexes);
        localAnnexes.forEach((item) => {
          let newItem: ImdrfAnnex | null = null;
          const parents = item.children;
          const newParents: ImdrfCodeWithOverrideDTO[] = [];

          if (parents && parents.length > 0) {
            //Level 1 codes
            parents.forEach((parent) => {
              let newParent = null;
              const newChildren: ImdrfCodeWithOverrideDTO[] = [];
              const children = parent.children;
              if (children && children.length > 0) {
                //Level 2 codes
                children.forEach((child) => {
                  let newChild: ImdrfCodeWithOverrideDTO | null = null;
                  let newGrandChildren: ImdrfCodeWithOverrideDTO[] = [];
                  const grandChildren = child.children;

                  //Level 3 codes
                  if (grandChildren && grandChildren.length > 0) {
                    newGrandChildren = codeListContainsSearchText(
                      grandChildren,
                      searchTerm,
                    );
                  }

                  if (newGrandChildren.length > 0) {
                    newChild = child;
                    newChild.children = newGrandChildren;
                    newChildren.push(newChild);
                    expanded.push(newChild.id.toString());
                  } else if (codeContainsSearchText(child, searchTerm)) {
                    newChildren.push(child);
                    expanded.push(child.id.toString());
                  }
                });
              }

              if (newChildren.length > 0) {
                newParent = parent;
                newParent.children = newChildren;
                newParents.push(newParent);
                expanded.push(newParent.id.toString());
              } else if (codeContainsSearchText(parent, searchTerm)) {
                newParents.push(parent);
                expanded.push(parent.id.toString());
              }
            });
          }

          if (newParents.length > 0) {
            newItem = {
              id: item.id,
              label: item.label,
              children: newParents,
            };
            filtered.push(newItem);
            expanded.push(newItem.id.toString());
          }
        });
        setItems(filtered);
        setExpandedItems(expanded);
      } else {
        setItems(annexes);
        setExpandedItems([]);
      }
    },
    [annexes, codeListContainsSearchText],
  );

  const debouncedFilter = useMemo(
    () => debounce(filterItems, 400),
    [filterItems],
  );

  const handleSearchChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    const searchTerm = event.target.value;
    setInputText(searchTerm);
    debouncedFilter(searchTerm);
  };

  function codeContainsSearchText(
    imdrfCode: ImdrfCodeWithOverrideDTO,
    searchTerm: string,
  ) {
    return (
      imdrfCode.code.toLowerCase().includes(searchTerm) ||
      imdrfCode.term.toLowerCase().includes(searchTerm) ||
      imdrfCode.definition.toLowerCase().includes(searchTerm) ||
      imdrfCode.nonImdrf?.toLowerCase().includes(searchTerm)
    );
  }

  useEffect(() => {
    async function fetchData(annexes: ImdrfAnnex[]) {
      await getAnnexesData(annexes, projectId);
      // set only unique items

      setItems((prev) => {
        const next = prev;

        for (const annex of annexes) {
          if (!next.includes(annex)) {
            next.push(annex);
          }
        }

        next.sort((a, b) => a.id.localeCompare(b.id));
        return next;
      });
    }

    async function fetchAllData(annexes: ImdrfAnnex[]) {
      fetchData(annexes);
    }

    if (annexes?.length > 0 && !annexDataFetched) {
      fetchAllData(annexes).then(() => {
        setAnnexes(annexes);
        setAnnexDataFetched(true);
      });
    }

    // disabling linter for this dependency array because adding getAnnexData causes this to re-fire infinitely
    // eslint-disable-next-line
  }, [annexes, annexDataFetched]);

  const richTreeItemProps: SlotComponentProps<
    typeof TreeItem,
    object,
    {
      itemId: TreeViewItemId;
      label: string;
    }
  > & {
    apiref: ApiRefType;
    searchtext: string;
  } = {
    apiref: apiRef,
    searchtext: inputText,
  };

  return (
    <Stack direction="column" spacing={1.5} className="categorize-modal">
      <TextField
        className="categorize-modal-textfield"
        label="IMDRF Code"
        fullWidth
        size="small"
        value={inputText}
        onChange={handleSearchChange}
      />
      {!annexDataFetched && (
        <Stack
          direction="column"
          spacing={1.5}
          alignItems={"center"}
          width="100%"
          style={{ marginTop: "25px" }}
        >
          <Avatar style={{ padding: "5px" }}>
            <CircularProgress />
          </Avatar>
          <Typography variant="h5" fontWeight="bold">
            Loading IMDRF Codes
          </Typography>
          <Typography variant="body2">
            Please wait while we load the IMDRF codes.
          </Typography>
        </Stack>
      )}
      {(annexDataFetched && items.length > 0 && (
        <HeightRestrictedTreeView
          apiRef={apiRef}
          expandedItems={expandedItems}
          expansionTrigger="iconContainer"
          onItemExpansionToggle={(event, itemId, isExpanded) => {
            // stop propagation to prevent the selected handler from triggering
            // when a user expands a selectable option
            event?.stopPropagation();
            if (isExpanded) {
              setExpandedItems([...expandedItems, `${itemId}`]);
            } else {
              setExpandedItems(expandedItems.filter((e) => e !== itemId));
            }
          }}
          selectedItems={String(selectedCode)}
          onSelectedItemsChange={(_event, itemId) => {
            if (itemId === null) {
              setSelectedCode(null);
            } else {
              const node = apiRef.current?.getItem(itemId);
              if (node.isSelectable) {
                setSelectedCode(node);
              }
            }
          }}
          items={items as (ImdrfAnnex | ImdrfCodeWithOverrideDTO)[]}
          getItemId={(item) => `${item.id}`}
          getItemLabel={(item) =>
            ("label" in item && item.label) ||
            ("term" in item && item.term) ||
            ""
          }
          slots={{ item: TreeItemWithLoading }}
          slotProps={{
            item: richTreeItemProps,
          }}
        />
      )) ||
        (annexDataFetched && (
          <Stack
            direction="column"
            spacing={1.5}
            alignItems={"center"}
            width="100%"
            style={{ marginTop: "25px" }}
          >
            <Avatar style={{ padding: "5px" }}>
              <HideSourceOutlined color="disabled" fontSize="large" />
            </Avatar>
            <Typography variant="h5" fontWeight="bold">
              No IMDRF Codes
            </Typography>
            <Typography variant="body2">
              There are no IMDRF codes that match this annex and term.
            </Typography>
          </Stack>
        ))}
    </Stack>
  );
}
